import IndicatorButton from 'components/indicator/IndicatorButton';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';
import { InvitationRefResponseType } from 'types/api/invitationRefResponseType';

/**
 *　招待受付画面
 */
export default function InvitationIndex(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { invitationCd } = useParams();

  const [acctState] = useRecoilState(AcctState);

  const { initializeAcctState, signUpRequest, signOutRequest } = useAppState();
  const { postInvitationRef, postInvitationAccept } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [invitationState, setInvitationState] = useState<InvitationRefResponseType>(null);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // 招待情報の取得
        const response = await postInvitationRef({
          invitationCd: invitationCd,
        });
        setInvitationState(response);

        // 招待されたメールアドレスとログイン中のメールアドレスが異なる場合はログアウトする
        if (acctState.user) {
          if (response.member.invitationMailAddress !== acctState.user.mailAddress) {
            await signOutRequest();
            await initializeAcctState(response.company.cd);
          }
        }
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　招待承認ボタン押下時の処理
   */
  const onAccept = () =>
    AppHelper.submit(processing, setProcessing, async () => {
      try {
        // ログイン済の場合は、招待受付APIを呼び出しホーム画面に遷移
        if (acctState.user) {
          await postInvitationAccept({
            invitationCd: invitationCd,
            user: null,
          });
          await initializeAcctState(invitationState.company.cd);
          navigate('/');
        }
        // 未ログインの場合は、認証コードを送信
        else {
          await signUpRequest(invitationState.member.invitationMailAddress);
          navigate('/signin/auth', {
            state: {
              mailAddress: invitationState.member.invitationMailAddress,
              nextTo: `/invitation/${invitationCd}/user`,
            },
          });
        }
      } catch (e) {
        showBoundary(e);
      }
    });

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized} />
      {/* 初期化後の表示 */}
      {initialized && (
        <>
          <main>
            <section className="section-404 vh-100">
              <div className="container">
                <div className="align-middle d-flex flex-column justify-content-center align-items-center">
                  <img
                    src="/img/common/logo.svg"
                    alt="BianQ ブランク"
                    className="d-block mx-auto card-logo"
                  />
                  {/* 招待が無効な場合 */}
                  {invitationState.company == null && <h3>この招待はすでに失効しています。</h3>}
                  {/* 招待が有効な場合 */}
                  {invitationState.company && (
                    <>
                      <h3>{invitationState.company.name}から招待を受けています。</h3>
                      <p className="text-center">参加するには招待を承認してください。</p>
                      <button
                        type="button"
                        onClick={onAccept}
                        className="btn btn-common d-inline-block moderate-blue"
                        disabled={processing}
                      >
                        <IndicatorButton visible={processing} />
                        招待を承認する
                      </button>
                    </>
                  )}
                </div>
              </div>
            </section>
          </main>
        </>
      )}
    </>
  );
}
